import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Body, Title } from "@vds/typography";
import { Checkbox } from "@vds/checkboxes";
import { Input } from "@vds/inputs";
import { DropdownSelect } from "@vds/selects";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { FormControl, FormControlLabel, Radio, RadioGroup, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { ButtonGroup, TextLink } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Badge } from "@vds/badges";
import { Modal, ModalBody, ModalTitle } from "@vds/modals";
import ColorBox from "../../ColorCheckBox/ColorBox";
import { colorMapping, getAdjustedAvailableLicenses } from "../../../utility/Utils";
import { sortedObject } from "../../../../utils";
import { Toggle } from "@vds/toggles";
import { CVAPP_PERSON_DETECTION } from "../../../constants/cvappConstants";
import { Notification } from "@vds/notifications";
import { MainSectionWrapper, CheckboxListFieldWrapper } from "./styles";
import { EnhancedToggleField } from "./EnhancedToggleField";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAdvancedConfigAppearances,
  setAdvancedConfigAppearances,
  setAdvancedConfigThresholds,
  setAdvancedConfigRadio
} from "../../../../store/actions";
import { EDIT_FLOW_NOTIFICATIONS, ADVANCED_CONFIG, LICENSE_MESSAGES } from "../../../../constants/enrolledAppearanceConstants";
import { isFeatureAccessible } from "../../../constants/licensesConstants";

const correctDefaultValue = (cvAppName, keyName, cvAppList = [], cvappid) => {
  let cvAppTemplate = cvAppList?.find((item) => item?.id === cvappid || item?.resources?.displayNameFull === cvAppName);
  return (
    getValue(
      cvAppTemplate?.configtemplate?.schema?.properties[ADVANCED_CONFIG.DEPLOY_PARAMETERS],
      keyName
    )?.default ?? null
  );
};

const getPositiveNumberRegex = (min) => {
  // If min is 0, allow 0 and positive numbers
  if (min === 0) {
    return /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
  }
  // If min is 1, allow only positive numbers >= 1
  return /^(?:[1-9]\d*)(?:\.\d+)?$/;
};
// Returns true if the numeric value is outside the schema's [minimum, maximum] range.
export const isOutOfRange = (value, schemaTemplateObject) => {
  if (value === "" || value === null || value === undefined) return false;
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  const min = schemaTemplateObject?.minimum;
  const max = schemaTemplateObject?.maximum;
  if (min !== undefined && min !== null && num < min) return true;
  if (max !== undefined && max !== null && num > max) return true;
  return false;
};

//range error message from schema min/max.
export const getFieldErrorText = (title, schemaTemplateObject) => {
  const min = schemaTemplateObject?.minimum;
  const max = schemaTemplateObject?.maximum;
  if (min !== undefined && max !== undefined) return `Enter a value from ${min} to ${max} for ${title}.`;
  if (min !== undefined) return `Enter a value of ${min} or greater for ${title}.`;
  if (max !== undefined) return `Enter a value of ${max} or less for ${title}.`;
  return `Enter a valid ${title}.`;
};
const handleErrorInput = (arr, key) => {
  if (Array.isArray(arr) && arr.includes(key)) {
    return arr;
  } else {
    arr.push(key);
    return arr;
  }
};

const handleErrorClear = (arr, key) => {
  if (Array.isArray(arr) && arr.includes(key)) {
    const index = arr.findIndex((item) => item === key);
    arr.splice(index, 1);
    return arr;
  } else {
    return arr;
  }
};

export const isPercentageFieldSchema = (schemaTemplateObject) =>
  schemaTemplateObject?.units === "decimal";

export const toPercentageDisplay = (raw) => {
  if (raw === "" || raw === undefined || raw === null) return raw;
  return String(parseFloat((parseFloat(raw) * 100).toPrecision(10)));
};

export const toDecimalStore = (inputValue) => {
  if (inputValue === "") return inputValue;
  return String(parseFloat(inputValue) / 100);
};

export const getUnitLabel = (schemaTemplateObject, title, siblingProperties) => {
  // If a sibling "units" enum selector exists in the parent object (duration-picker
  // pattern, e.g. min_association_time = { units: [seconds/hours/minutes], min_proximity_period }),
  // the real unit is user-selectable via that dropdown — this field's own static
  // units metadata would be redundant/misleading, so suppress it.
  if (Array.isArray(siblingProperties?.units?.enum)) {
    return "";
  }
  // Explicit server-provided unit (any field type) — "decimal" is the internal
  // percentage signal (NPDIOTS-9258), handled separately via isPercentageFieldSchema.
  // No title-based fallback: verified backend always sends explicit `units` for
  // every field whose title implies a unit, across all templates (v1/v2, all apps).
  if (schemaTemplateObject?.units && schemaTemplateObject?.units !== "decimal") {
    return schemaTemplateObject.units;
  }
  return "";
};

// English grammar: "1 second" not "1 seconds". Applies to any unit string
// (backend contract now always sends plural)
const IRREGULAR_PLURALS = { feet: "foot" };

export const singularizeUnit = (unit, value) => {
  if (!unit) return unit;
  const numeric = parseFloat(value);
  if (Number.isNaN(numeric) || Math.abs(numeric) !== 1) return unit;
  if (IRREGULAR_PLURALS[unit]) return IRREGULAR_PLURALS[unit];
  return unit.endsWith("s") ? unit.slice(0, -1) : unit;
};

// Resolves the seed value for a deploy-parameter field from its schema default.
// NPDIOTS-9546: previously any falsy-but-unset default (null/undefined) fell through
// to a hardcoded "" fallback, which is invalid for non-string fields (e.g. max.occupancy)
// and gets silently sent to the backend for display:false fields the user never edits,
// causing a 400 on deploy. When there's no real default, omit the field instead of
// guessing "" — hasValue:false tells the caller to skip seeding this key.
export const resolveDefaultFieldValue = (scemaTemplateData) => {
  const { default: defaultValue } = scemaTemplateData || {};
  if (defaultValue === null || defaultValue === undefined) {
    return { hasValue: false };
  }
  if (Array.isArray(defaultValue)) {
    return { hasValue: true, value: [...defaultValue] };
  }
  return { hasValue: true, value: defaultValue };
};

// Backfills schema keys that are missing from a previously-saved deploy-parameters
// blob (e.g. this ROI's saved config predates a schema field, or only contains keys
// from a different form section) without overwriting any already-saved/edited value.
// Returns the merged params object, or null when nothing is missing (so callers can
// skip a redundant setState/re-render).
export const mergeMissingDefaults = (existingParams = {}, payloadParams = {}) => {
  const missingKeys = Object.keys(payloadParams).filter((k) => !(k in existingParams));
  if (missingKeys.length === 0) return null;
  return {
    ...existingParams,
    ...Object.fromEntries(missingKeys.map((k) => [k, payloadParams[k]])),
  };
};

export const TextInputField = ({
  schemaTemplateObject,
  className,
  title,
  setPayload,
  payload,
  keyName,
  parameterKeyName,
  style,
  setIsTampered,
  setErrorInFormInput,
  cvAppName,
  description,
  cvAppList = [],
  cvappid
}) => {
  const isPercentageField = isPercentageFieldSchema(schemaTemplateObject);
  // For percentage fields, assume 0–1 range when schema omits min/max
  const percentageSchema = isPercentageField
    ? {
        ...schemaTemplateObject,
        minimum: schemaTemplateObject?.minimum ?? 0,
        maximum: schemaTemplateObject?.maximum ?? 1,
      }
    : schemaTemplateObject;
  const helperTextUnit = getUnitLabel(schemaTemplateObject, title);

  const [isShowTextObject, setIsShowTextObject] = useState(true);

  useEffect(() => {
    let newPayload = payload;
    if (schemaTemplateObject.hasOwnProperty("properties")) {
      if (schemaTemplateObject["properties"]["if"]) {
        if (
          typeof schemaTemplateObject["properties"]["if"]["properties"] ===
          "object"
        ) {
          for (let [keyName, value] of Object.entries(
            schemaTemplateObject["properties"]["if"]["properties"]
          )) {
            if (getValue(payload, keyName) === value?.const) {
              for (let [secondKeyName, secondValue] of Object.entries(
                schemaTemplateObject["properties"]["then"]["properties"]
              )) {
                // Use the actual value from payload if const is not "undefined"
                const valueToSet = secondValue?.const === "undefined"
                  ? undefined 
                  : (payload?.config?.[parameterKeyName]?.[secondKeyName] !== undefined 
                      ? payload.config[parameterKeyName][secondKeyName] 
                      : secondValue?.const);
                
                newPayload = {
                  ...payload,
                  config: {
                    ...payload.config,
                    [parameterKeyName]: {
                      ...(payload.config?.[parameterKeyName] || {}),
                      [secondKeyName]: valueToSet,
                    },
                  },
                };
                if (
                  JSON.stringify(payload) !== JSON.stringify(newPayload) &&
                  typeof getValue(payload, secondKeyName) !== "string"
                ) {
                  setPayload(newPayload);
                }
              }
            } else if (
              schemaTemplateObject["properties"].hasOwnProperty("else")
            ) {
              for (let [secondKeyName, secondValue] of Object.entries(
                schemaTemplateObject["properties"]["else"]["properties"]
              )) {
                // Use the actual value from payload if const is not "undefined"
                const valueToSet = secondValue?.const === "undefined" 
                  ? undefined 
                  : (payload?.config?.[parameterKeyName]?.[secondKeyName] !== undefined 
                      ? payload.config[parameterKeyName][secondKeyName] 
                      : secondValue?.const);
                
                newPayload = {
                  ...payload,
                  config: {
                    ...payload.config,
                    [parameterKeyName]: {
                      ...payload.config[parameterKeyName],
                      [secondKeyName]: valueToSet,
                    },
                  },
                };
                if (
                  JSON.stringify(payload) !== JSON.stringify(newPayload) &&
                  typeof getValue(payload, secondKeyName) !== "string"
                ) {
                  setPayload(newPayload);
                }
              }
            }
          }
        }
      }
      if (getValue(payload, keyName) === undefined) {
        setIsShowTextObject(false);
      } else {
        setIsShowTextObject(true);
      }
    }
  }, [payload]);

  if (!isShowTextObject) {
    return;
  }

  return (
    <div className={className} style={{ ...style }}>
      <Input
        type={
          schemaTemplateObject?.type === "integer"
            ? "number"
            : schemaTemplateObject?.type === "string"
            ? "text"
            : "text"
        }
        label={
          <div className="main-section-label-with-info">
            <div>{title}</div>
            <div>
              <Tooltip title={description} >
                <InfoOutlined fontSize={"small"} />
              </Tooltip>
            </div>
          </div>
        }
        value={(() => {
          const raw = payload?.config?.[parameterKeyName]?.[keyName];
          if (isPercentageField) return toPercentageDisplay(raw);
          return raw === 0 ? "0" : raw;
        })()}
        onChange={(e) => {
          const inputValue = e.target.value.toString();
          const hasEnum = Array.isArray(schemaTemplateObject?.enum) && schemaTemplateObject.enum?.length > 0;
          // For decimal fields: user types percentage, store as decimal
          const storeValue = isPercentageField ? toDecimalStore(inputValue) : e.target.value;
          const validationValue = isPercentageField ? toDecimalStore(inputValue) : inputValue;

          if (hasEnum) {
            // For enum fields, allow typing but validate against enum values
            setPayload({
              ...payload,
              config: {
                ...payload.config,
                [parameterKeyName]: {
                  ...payload.config[parameterKeyName],
                  [keyName]: e.target.value,
                },
              },
            });
            setIsTampered(true);
            if (inputValue === "" || !schemaTemplateObject.enum.map(String).includes(inputValue)) {
              setErrorInFormInput((prev) => handleErrorInput(prev, keyName));
            } else {
              setErrorInFormInput((prev) => handleErrorClear(prev, keyName));
            }
          } else if (
            inputValue === "" ||
            getPositiveNumberRegex(schemaTemplateObject?.minimum ?? 0).test(inputValue)
          ) {
            setPayload({
              ...payload,
              config: {
                ...payload.config,
                [parameterKeyName]: {
                  ...payload.config[parameterKeyName],
                  [keyName]: storeValue,
                },
              },
            });
            setIsTampered(true);
            // Mark field as error if empty or value falls outside schema min/max bounds
            if (inputValue === "" || isOutOfRange(validationValue, percentageSchema)) {
              setErrorInFormInput((prev) => handleErrorInput(prev, keyName));
            } else {
              setErrorInFormInput((prev) => handleErrorClear(prev, keyName));
            }
          }
        }}
        disabled={false}
        error={
          payload?.config?.[parameterKeyName]?.[keyName] === "" ||
          isOutOfRange(payload?.config?.[parameterKeyName]?.[keyName], percentageSchema) ||
          (Array.isArray(schemaTemplateObject?.enum) &&
            schemaTemplateObject.enum.length > 0 &&
            payload?.config?.[parameterKeyName]?.[keyName] !== "" &&
            payload?.config?.[parameterKeyName]?.[keyName] !== undefined &&
            !schemaTemplateObject.enum.map(String).includes(payload?.config?.[parameterKeyName]?.[keyName]?.toString()))
        }
        errorText={
          Array.isArray(schemaTemplateObject?.enum) && schemaTemplateObject.enum?.length > 0
            ? `Allowed values: ${schemaTemplateObject.enum.join(", ")}`
            : isPercentageField
            ? getFieldErrorText(title, {
                ...percentageSchema,
                minimum: percentageSchema.minimum * 100,
                maximum: percentageSchema.maximum * 100,
              })
            : getFieldErrorText(title, schemaTemplateObject)
        }
        helperText={(() => {
          const rawDefault = correctDefaultValue(cvAppName, keyName, cvAppList, cvappid) ?? schemaTemplateObject?.default;
          const displayDefault =
            isPercentageField && rawDefault !== undefined && rawDefault !== null
              ? parseFloat(toPercentageDisplay(rawDefault))
              : rawDefault;
          const unit = singularizeUnit(helperTextUnit, displayDefault);
          return unit
            ? `Default: ${displayDefault} ${unit}`
            : isPercentageField
            ? `Default: ${displayDefault} %`
            : `Default: ${displayDefault}`;
        })()}
        helperTextPlacement="bottom"
        // tooltip={{
        //   title: <div style={{zIndex: 10000}}>hi</div>,
        //   children: title,
        // }}
      />
    </div>
  );
};

export const SelectInputField = ({
  schemaTemplateObject,
  className,
  title,
  keyName,
  setPayload,
  payload,
  parameterKeyName,
  style,
  setIsTampered,
}) => {
  return (
    <div className={className} style={{ ...style }}>
      <DropdownSelect
        label={title}
        errorText={`Select a ${title}`}
        error={false}
        disabled={false}
        readOnly={false}
        inlineLabel={false}
        onChange={(e) => {
          setPayload({
            ...payload,
            config: {
              ...payload.config,
              [parameterKeyName]: {
                ...payload.config[parameterKeyName],
                [keyName]: e.target.value,
              },
            },
          });
          setIsTampered(true);
        }}
      >
        {schemaTemplateObject?.enum?.map((item) => {
          return (
            <option
              key={item}
              selected={payload?.config?.[parameterKeyName]?.[keyName]}
            >
              {item}
            </option>
          );
        })}
      </DropdownSelect>
    </div>
  );
};

export const getValue = (obj, key) => {
  if (typeof obj !== "object" || obj === null) return null;
  if (key in obj) return obj[key];

  for (const k in obj) {
    if (typeof obj[k] === "object" && obj[k] !== null) {
      const value = getValue(obj[k], key);
      if (value !== null) return value;
    }
  }
  return null;
};

export const cvAppRequiredParams = (cvAppName = "", cvAppList = [], cvappid) => {
  const cvAppOfInterest = cvAppList?.find(item => item?.id === cvappid);
  const requiredParams = cvAppOfInterest?.configtemplate?.schema?.properties?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.required;
  return requiredParams ?? [];
};

export const CheckboxListField = ({
  schemaTemplateObject,
  title,
  keyName,
  setPayload,
  payload,
  parameterKeyName,
  style,
  setIsTampered,
  setErrorInFormInput = () => {},
  cvAppName = "",
  cvAppList = [],
  cvappid
}) => {
  const result = cvAppRequiredParams(cvAppName, cvAppList, cvappid);
  const onChangeHandler = (e, item) => {
    if (e.target.checked) {
      if (Array.isArray(payload?.config[parameterKeyName][keyName])) {
        payload?.config[parameterKeyName][keyName].push(item);
      } else {
        payload.config[parameterKeyName][keyName] = [];
        payload?.config[parameterKeyName][keyName].push(item);
      }
    } else {
      const index = payload?.config[parameterKeyName][keyName].indexOf(item);
      payload?.config[parameterKeyName][keyName].splice(index, 1);
    }
    if (
      result.includes(keyName) &&
      Array.isArray(payload?.config[parameterKeyName][keyName]) &&
      payload?.config[parameterKeyName][keyName].length === 0
    ) {
      setErrorInFormInput((prev) => handleErrorInput(prev, keyName));
    } else {
      setErrorInFormInput((prev) => handleErrorClear(prev, keyName));
    }
    setPayload({
      ...payload,
    });
    setIsTampered(true);
  };

  const getMaxHeight = () => {
    if (getValue(schemaTemplateObject, "enum")?.length === 2) {
      return "auto";
    }
    return `${(getValue(schemaTemplateObject, "enum")?.length / 2) * 40}px`;
  };

  useEffect(() => {
    if (
      result.includes(keyName) &&
      (getValue(payload, keyName) === null ||
        getValue(payload, keyName) === "" ||
        (Array.isArray(getValue(payload, keyName)) &&
          getValue(payload, keyName).length === 0))
    ) {
      setErrorInFormInput((prev) => handleErrorInput(prev, keyName));
    } else {
      setErrorInFormInput((prev) => handleErrorClear(prev, keyName));
    }
  }, []);

  return (
    <CheckboxListFieldWrapper maxHeight={getMaxHeight()} style={{ ...style }}>
      <Body size="large">
        <div className="main-section-title">{title}</div>
      </Body>
      <div className="vision_config-checkbox-list-wrapper">
        {getValue(schemaTemplateObject, "enum")?.map((item) => {
          return (
            <div key={item} className="vision_d-flex-1">
              <Checkbox
                name={item}
                width="auto"
                disabled={false}
                error={false}
                selected={payload?.config?.[parameterKeyName]?.[
                  keyName
                ]?.includes(item) ? true : false}
                onChange={(e) => {
                  onChangeHandler(e, item);
                }}
              >
                {item}
              </Checkbox>
            </div>
          );
        })}
      </div>
    </CheckboxListFieldWrapper>
  );
};

export const CheckboxField = ({
  schemaTemplateObject,
  title,
  keyName,
  setPayload,
  payload,
  additionalInfo,
  parameterKeyName,
  style,
  setIsTampered,
}) => {
  return (
    <CheckboxListFieldWrapper style={{ ...style }}>
      <Checkbox
        name="default"
        width="auto"
        label={title}
        disabled={false}
        error={false}
        selected={payload?.config?.[parameterKeyName]?.[keyName] ? true : false}
        onChange={(e) => {
          setPayload({
            ...payload,
            config: {
              ...payload.config,
              [parameterKeyName]: {
                ...payload.config[parameterKeyName],
                [keyName]:
                  schemaTemplateObject?.type === "boolean"
                    ? e.target.checked
                    : e.target.value,
              },
            },
          });
          setIsTampered(true);
        }}
      >
        {additionalInfo}
      </Checkbox>
    </CheckboxListFieldWrapper>
  );
};

export const ToggleField = ({
  schemaTemplateObject,
  title,
  keyName,
  setPayload,
  payload,
  additionalInfo,
  parameterKeyName,
  style,
  setIsTampered,
  setErrorInFormInput,
  children,
  action,
  schemaData,
  disabled: disabledProp = false,
  licenseInfo
}) => {
  const dispatch = useDispatch();
  
  // Apply margin-bottom conditionally: no margin when color field renders below
  const isColorToggleOn = keyName === ADVANCED_CONFIG.ENABLE_APPEARANCE && payload?.config?.[parameterKeyName]?.[keyName];
  const marginBottom = isColorToggleOn ? "0" : "20px";

  return (
    <div className="main-section-toggle-container" style={{ marginBottom }}>
    <div className="main-section-toggle-row">
    <Toggle
      on={
        keyName === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE 
          ? (payload?.config?.[parameterKeyName]?.[keyName] || !!payload?.config?.[parameterKeyName]?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS])
          : payload?.config?.[parameterKeyName]?.[keyName] ? true : false
      }
      surface="light"
      surfaceType="colorFill"
      showText={true}
      textWeight="regular"
      textSize="large"
      textPosition="right"
      onChange={(e) => {
        const isChecked = e.target.checked;
        
        // Handle mutual exclusivity between enable_appearance and enable_enrolled_appearance
        let updatedConfig = {
          ...payload.config[parameterKeyName],
          [keyName]: schemaTemplateObject?.type === "boolean" ? isChecked : e.target.value,
        };

        // Helper to clear enrolled appearance parameters from payload and Redux
        const clearEnrolledAppearanceState = () => {
          delete updatedConfig[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS];
          dispatch(clearAdvancedConfigAppearances());
        };

        // If enabling one, disable the other and clear Redux state
        if (isChecked) {
          if (keyName === ADVANCED_CONFIG.ENABLE_APPEARANCE) {
            clearEnrolledAppearanceState();
            if (payload?.config?.[parameterKeyName]?.[ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE]) {
              updatedConfig[ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE] = false;
              setErrorInFormInput((prev) => handleErrorClear(prev, ADVANCED_CONFIG.APPEARANCES));
            }
          } else if (keyName === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && payload?.config?.[parameterKeyName]?.[ADVANCED_CONFIG.ENABLE_APPEARANCE]) {
            updatedConfig[ADVANCED_CONFIG.ENABLE_APPEARANCE] = false;
            delete updatedConfig[ADVANCED_CONFIG.APPEARANCE_NAME];
            delete updatedConfig[ADVANCED_CONFIG.APPEARANCE_ID];
            delete updatedConfig[ADVANCED_CONFIG.IN_APPEARANCE];
            setErrorInFormInput((prev) => handleErrorClear(prev, ADVANCED_CONFIG.COLORS));
          }
        } else if (!isChecked && keyName === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE) {
          clearEnrolledAppearanceState();
        }
        const newValue = schemaTemplateObject?.type === "boolean"
          ? Boolean(e.target.checked)
          : e.target.value;

        // Handle conditional logic from schema
        // Check if any other fields have conditional dependencies on this field
        if (schemaData?.scemaTemplateData) {
          Object.entries(schemaData.scemaTemplateData).forEach(([fieldKey, fieldSchema]) => {
            if (fieldSchema?.properties?.if?.properties && fieldSchema.properties.if.properties[keyName]) {
              const ifCondition = fieldSchema.properties.if.properties[keyName].const;
              
              if (newValue === ifCondition && fieldSchema.properties.then?.properties) {
                // Apply 'then' branch
                Object.entries(fieldSchema.properties.then.properties).forEach(([targetKey, targetValue]) => {
                  updatedConfig[targetKey] = targetValue?.const === "undefined" ? undefined : targetValue?.const;
                });
              } else if (newValue !== ifCondition && fieldSchema.properties.else?.properties) {
                // Apply 'else' branch
                Object.entries(fieldSchema.properties.else.properties).forEach(([targetKey, targetValue]) => {
                  updatedConfig[targetKey] = targetValue?.const === "undefined" ? undefined : targetValue?.const;
                });
              }
            }
          });
        }

        setPayload({
          ...payload,
          config: {
            ...payload.config,
            [parameterKeyName]: updatedConfig,
          },
        });
        setIsTampered(true);
        
        // Handle validation for both toggles
        if (keyName === ADVANCED_CONFIG.ENABLE_APPEARANCE) {
          if (e.target.checked) {
            // Initialize colors array if it doesn't exist for validation
            if (!updatedConfig[ADVANCED_CONFIG.COLORS]) {
              updatedConfig[ADVANCED_CONFIG.COLORS] = [];
            }
            setErrorInFormInput((prev) =>
              handleErrorInput(prev, ADVANCED_CONFIG.COLORS)
            );
          } else {
            setErrorInFormInput((prev) =>
              handleErrorClear(prev, ADVANCED_CONFIG.COLORS)
            );
          }
        } else if (keyName === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE) {
          if (e.target.checked) {
            setErrorInFormInput((prev) =>
              handleErrorInput(prev, ADVANCED_CONFIG.APPEARANCES)
            );
          } else {
            setErrorInFormInput((prev) =>
              handleErrorClear(prev, ADVANCED_CONFIG.APPEARANCES)
            );
          }
        }
      }}
      statusText={() => <Body size="medium">{title}</Body>}
      disabled={disabledProp}
    />
    {licenseInfo}
    </div>
    {children}
    </div>
  );
};

export const RadioField = ({
  schemaTemplateObject,
  title,
  keyName,
  setPayload,
  payload,
  additionalInfo,
  parameterKeyName,
  style,
  schemaData,
  setSchemaData,
  setIsTampered,
}) => {
  return (
    <CheckboxListFieldWrapper style={{ ...style }}>
      <RadioButtonGroup
        onChange={(e) => {
          var obj2 = {};
          Object.keys(schemaData.scemaTemplateData).map((f) => {
            if (schemaData.scemaTemplateData[f].type == "boolean") {
              f != e.target.value
                ? (schemaData.scemaTemplateData[f].default = false)
                : (schemaData.scemaTemplateData[f].default = true);
            }
            obj2[f] = schemaData.scemaTemplateData[f];
            return { [f]: schemaData.scemaTemplateData[f] };
          });

          setSchemaData({ ...schemaData, scemaTemplateData: obj2 });
          let boolPayload = {};
          Object.keys(obj2).map((item) => {
            if (obj2[item]?.type === "boolean") {
              boolPayload[item] = obj2[item]?.default;
            }
            return { item: obj2[item] };
          });

          setPayload({
            ...payload,
            config: {
              ...payload.config,
              [parameterKeyName]: {
                ...payload.config[parameterKeyName],
                // [keyName]:
                // schemaTemplateObject?.type === "boolean"
                //   ? e.target.checked
                //   : schemaData.scemaTemplateData[e.target.value].default,
                ...boolPayload,
              },
            },
          });
          setIsTampered(true);
        }}
        error={false}
        value={schemaTemplateObject?.default === true ? title : "No"}
        data={[
          {
            label: schemaTemplateObject?.description,
            children: additionalInfo,
            value: title,
            ariaLabel: title,
            disabled: false,
          },
        ]}
      />
    </CheckboxListFieldWrapper>
  );
};

export const ColorField = ({
  colorArray,
  payload,
  setPayload,
  keyName,
  parameterKeyName,
  title,
  setIsTampered,
  cvAppName,
  setErrorInFormInput,
}) => {
  useEffect(() => {
    if (getValue(payload, ADVANCED_CONFIG.IS_COLOR_EXCLUSION_SET) === null) {
      setPayload({
        ...payload,
        config: {
          ...payload.config,
          [parameterKeyName]: {
            ...payload.config[parameterKeyName],
            [ADVANCED_CONFIG.IS_COLOR_EXCLUSION_SET]: false,
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    if (
      getValue(payload, ADVANCED_CONFIG.COLORS) &&
      Array.isArray(getValue(payload, ADVANCED_CONFIG.COLORS)) &&
      getValue(payload, ADVANCED_CONFIG.COLORS).length === 0
    ) {
      setErrorInFormInput((prev) => handleErrorInput(prev, ADVANCED_CONFIG.COLORS));
    } else if(getValue(payload, ADVANCED_CONFIG.COLORS)){
      
      setErrorInFormInput((prev) => handleErrorClear(prev, ADVANCED_CONFIG.COLORS));

    }

    return () => {
      setErrorInFormInput((prev) => handleErrorClear(prev, ADVANCED_CONFIG.COLORS));
    };
  }, [payload]);

  return (
    <div className="main-section-color-field-container">
      <FormControl>
        <RadioGroup
          aria-labelledby="demo-controlled-radio-buttons-group"
          name="controlled-radio-buttons-group"
          value={
            getValue(payload, ADVANCED_CONFIG.IS_COLOR_EXCLUSION_SET)
              ? getValue(payload, ADVANCED_CONFIG.IS_COLOR_EXCLUSION_SET)
              : "false"
          }
          className="main-section-radio-group-column"
          onChange={(e) => {
            setIsTampered(true);
            setPayload({
              ...payload,
              config: {
                ...payload.config,
                [parameterKeyName]: {
                  ...payload.config[parameterKeyName],
                  [ADVANCED_CONFIG.IS_COLOR_EXCLUSION_SET]:
                    e.target.value === "true" ? true : false,
                },
              },
            });
          }}
        >
          <FormControlLabel
            value="true"
            control={<Radio sx={{ "&.Mui-checked": { color: "black" } }} />}
            label={
              <div>
                <div>
                  Alert me when a{" "}
                  {cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? "vehicle"
                    : "person"}{" "}
                  <Body bold primitive="span" size="large">IS NOT</Body>{" "}
                  {cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? ""
                    : "wearing "}{" "}
                  one of the selected colors
                </div>
                <div
                  style={{
                    fontWeight: 200,
                    fontSize: "0.85rem",
                    color: "grey",
                  }}
                >
                  {`Ex: Select "dark green" to receive alerts for all 
                  ${cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? "vehicle "
                    : ""}
                  colors but "dark green"`}
                </div>
              </div>
            }
          />
          <FormControlLabel
            value="false"
            control={<Radio sx={{ "&.Mui-checked": { color: "black" } }} />}
            label={
              <div>
                <div>
                  Alert me when a{" "}
                  {cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? "vehicle"
                    : "person"}{" "}
                  <Body bold primitive="span" size="large">IS</Body>{" "}
                  {cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? ""
                    : "wearing "}{" "}
                  one of the selected colors
                </div>
                <div
                  style={{
                    fontWeight: 200,
                    fontSize: "0.85rem",
                    color: "grey",
                  }}
                >
                  {`Ex: Select "dark green" to receive alerts when a 
                  ${cvAppName.split(" ")[0].toLowerCase() === "vehicle"
                    ? "vehicle is "
                    : "person is wearing "}
                  "dark green"`}
                </div>
              </div>
            }
          />
        </RadioGroup>
      </FormControl>
      <div className="main-section-color-box-container">
        {Array.isArray(colorArray) &&
          colorArray.map((color, index) => (
            <div key={`${index}-${color}`}>
              <ColorBox
                color={colorMapping(color)}
                onChange={(e) => {
                  const result = () => {
                    const arr = payload?.config?.[parameterKeyName]?.[keyName];
                    if (e.target.checked) {
                      return arr === ""
                        ? [color]
                        : Array.isArray(arr)
                        ? [...arr, color]
                        : [];
                    } else {
                      if (Array.isArray(arr)) {
                        const index = arr.indexOf(color);
                        if (index > -1) {
                          arr.splice(index, 1);
                        }
                        return arr.length > 0 ? arr : [];
                      } else {
                        return [];
                      }
                    }
                  };
                  setPayload({
                    ...payload,
                    config: {
                      ...payload.config,
                      [parameterKeyName]: {
                        ...payload.config[parameterKeyName],
                        [keyName]: result(),
                      },
                    },
                  });
                  setIsTampered(true);
                }}
                checked={payload?.config?.[
                  ADVANCED_CONFIG.DEPLOY_PARAMETERS
                ]?.colors?.includes(color)}
              />
            </div>
          ))}
      </div>
      {payload?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.colors?.length === 0 ? (
        <div className="main-section-color-selection-text">Selected : <b>no colors selected</b></div>
      ) : Array.isArray(payload?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.colors) ? (
        <div className="main-section-color-selection-text">
          ({payload?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.colors?.length} selected)
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export const ObjectGroup = ({
  schemaTemplateObject,
  cvAppName,
  keyName,
  payload,
  setPayload,
  parameterKeyName,
  setIsTampered,
  setErrorInFormInput,
  cvAppList = [],
  cvappid
}) => {
  return (
    <div className="main-section-object-group">
      {Object.keys(sortedObject(schemaTemplateObject?.properties))
        .filter((key) => schemaTemplateObject?.properties[key]?.display !== false)
        .map((key) => {
        let cvAppTemplate = cvAppList?.find((item) => item?.id === cvappid);
        if (schemaTemplateObject?.properties[key]?.type === "integer") {
          const fieldSchema = schemaTemplateObject?.properties[key];
          return (
            <div key={key} className="main-section-field-width-45">
              <Input
                type={"text"}
                label={
                  <div className="main-section-label-with-info">
                    <div>{schemaTemplateObject?.properties[key]?.title}</div>
                    <div>
                      <Tooltip
                        title={schemaTemplateObject?.properties[key]?.title}
                      >
                        <InfoOutlined fontSize={"small"} />
                      </Tooltip>
                    </div>
                  </div>
                }
                value={
                  payload?.config[parameterKeyName]?.[keyName]?.[key] === 0
                    ? "0"
                    : payload?.config[parameterKeyName]?.[keyName]?.[key] ?? "0"
                }
                onChange={(e) => {
                  const val = e.target.value.toString();
                  if (
                    val === "" ||
                    getPositiveNumberRegex(fieldSchema?.minimum ?? 0).test(val)
                  ) {
                    setPayload({
                      ...payload,
                      config: {
                        ...payload.config,
                        [parameterKeyName]: {
                          ...payload.config[parameterKeyName],
                          [keyName]: {
                            ...payload.config[parameterKeyName][keyName],
                            [key]: e.target.value,
                          },
                        },
                      },
                    });
                    setIsTampered(true);
                    // Mark error if empty or value falls outside schema min/max bounds
                    if (val === "" || isOutOfRange(val, fieldSchema)) {
                      setErrorInFormInput((prev) => handleErrorInput(prev, keyName));
                    } else {
                      setErrorInFormInput((prev) => handleErrorClear(prev, keyName));
                    }
                  }
                }}
                disabled={false}
                error={
                  payload?.config[parameterKeyName]?.[keyName]?.[key] === "" ||
                  isOutOfRange(payload?.config[parameterKeyName]?.[keyName]?.[key], fieldSchema)
                }
                errorText={getFieldErrorText(fieldSchema?.title, fieldSchema)}
                helperText={(() => {
                  const defaultVal =
                    getValue(
                      cvAppTemplate?.configtemplate?.schema?.properties[
                        ADVANCED_CONFIG.DEPLOY_PARAMETERS
                      ]?.properties?.[keyName],
                      key
                    ) ?? "";
                  const unit = singularizeUnit(
                    getUnitLabel(fieldSchema, fieldSchema?.title, schemaTemplateObject?.properties),
                    defaultVal
                  );
                  if (!unit) return `Default: ${defaultVal}`;
                  return defaultVal === ""
                    ? `Default: ${unit}`
                    : `Default: ${defaultVal} ${unit}`;
                })()}
                helperTextPlacement="bottom"
              />
            </div>
          );
        }

        if (schemaTemplateObject?.properties[key]?.type === "string") {
          return (
            <div key={key} className="main-section-field-width-45-margin">
              <DropdownSelect
                label={schemaTemplateObject?.properties[key]?.title ?? schemaTemplateObject?.properties[key]?.description}
                errorText={`Select a ${schemaTemplateObject?.properties[key]?.title}`}
                error={false}
                disabled={false}
                readOnly={false}
                inlineLabel={false}
                onChange={(e) => {
                  setPayload({
                    ...payload,
                    config: {
                      ...payload.config,
                      [parameterKeyName]: {
                        ...payload.config[parameterKeyName],
                        [keyName]: {
                          ...payload.config[parameterKeyName][keyName],
                          [key]: e.target.value,
                        },
                      },
                    },
                  });
                  setIsTampered(true);
                }}
              >
                {<option>{payload?.config?.[parameterKeyName]?.[keyName]?.[key] ?? schemaTemplateObject?.properties[key]?.enum[0]}</option>}
                {schemaTemplateObject?.properties[key]?.enum?.filter(item => item !== payload?.config?.[parameterKeyName]?.[keyName]?.[key]).map((item) => {
                  return <option key={item}>{item}</option>;
                })}
              </DropdownSelect>
            </div>
          );
        }
      })}
    </div>
  );
};

const isEnableColorClicked = (advancedData) => {
  if (
    advancedData &&
    advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS].hasOwnProperty(
      ADVANCED_CONFIG.ENABLE_APPEARANCE
    )
  ) {
    return advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.[ADVANCED_CONFIG.ENABLE_APPEARANCE]
      ? true
      : false;
  }
  return true;
};

const isEnrolledAppearanceClicked = (advancedData) => {
  if (
    advancedData &&
    advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS].hasOwnProperty(
      ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE
    )
  ) {
    return advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.[ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE]
      ? true
      : false;
  }
  return false;
};

export const NotificationContainer = ({ title, subtitle }) => {
  return (
    <Notification
      type="info"
      title={title}
      subtitle={subtitle}
      surface="light"
      fullBleed={false}
      inline={false}
      disableFocus={true}
      hideCloseButton={true}
      layout="vertical"
    />
  );
};

export const MainSection = ({
  data,
  parameterKeyName,
  advancedData,
  setAdvancedData,
  setIsTampered,
  cvAppName,
  cvappid,
  setErrorInFormInput,
  setIsClick,
  isClick,
  action,
  originalDeployConfig = {},
  siteId={siteId}
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [schemaData, setSchemaData] = useState({ ...data });
  const [showNavigationModal, setShowNavigationModal] = useState(false);

  // Check original API deploy data (not affected by unsaved user changes)
  const wasEADeployed = !!originalDeployConfig?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS];
  const hadColorsDeployed = Array.isArray(originalDeployConfig?.[ADVANCED_CONFIG.COLORS]) && originalDeployConfig?.[ADVANCED_CONFIG.COLORS]?.length > 0;

  // Get licenses from reducer
  const licensesFromReducer = useSelector((state) => state.licenseReducer.licenses);
  const isV1OnlyAccount = useSelector((state) => state.licenseReducer.isV1OnlyAccount);
  const isEaFlagEnabled = window.REACT_APP_ENROLLED_APPEARANCE === "true";
  const { cvAppList } = useSelector((state) => state?.cvAppReducer);
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);

  // Get the enrolled appearance license
  const enrolledAppearanceLicense = licensesFromReducer?.find(l => l?.displayName === "Enrolled Appearance");
  const hasAvailableLicenses = enrolledAppearanceLicense?.availablelicenses && enrolledAppearanceLicense.availablelicenses > 0;

  // License deducts as soon as the Enrolled Appearance toggle is switched on, not on appearance selection
  const isEAToggleOn = isEnrolledAppearanceClicked(advancedData);
  const adjustedAvailableLicenses = getAdjustedAvailableLicenses(
    enrolledAppearanceLicense,
    isEAToggleOn,
    action,
    wasEADeployed,
    ADVANCED_CONFIG.TOTAL_LICENSES_AVAILABLE
  );

  // Handler for license navigation - shows confirmation modal first
  const handleLicenseNavigation = () => {
    setShowNavigationModal(true);
  };

  // Handler for confirmed navigation
  const handleConfirmNavigation = () => {
    if (enrolledAppearanceLicense) {
      const selectedLicenseRecord = {
        ...enrolledAppearanceLicense,
        license: enrolledAppearanceLicense.sku,
        purchased: enrolledAppearanceLicense.licenses,
        used: enrolledAppearanceLicense.usedlicenses,
        available: enrolledAppearanceLicense.availablelicenses,
        sites: "0 sites",
        billingAccount: activeSession?.accountId
      };
      sessionStorage.setItem("selectedLicenseRecord", JSON.stringify(selectedLicenseRecord));
      sessionStorage.setItem("licenses", JSON.stringify(licensesFromReducer));

      dispatch(clearAdvancedConfigAppearances());

      navigate("/licensedetails");
    }
    setShowNavigationModal(false);
  };

  // Handler for canceling navigation
  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
  };

  useEffect(() => {
    const payload = {
      config: {
        [parameterKeyName]: {},
      },
    };
    data?.scemaResourceData?.forEach((item) => {
      // Skip fields that should only exist inside enrolled_appearance_parameters
      const fieldsToSkip = ADVANCED_CONFIG.FIELDS_TO_SKIP;
      if (fieldsToSkip.includes(item?.key)) {
        return; // Don't add these to top-level payload
      }
      
      const scemaTemplateData = data?.scemaTemplateData[item?.key];
      if (isClick) {
          payload["config"][parameterKeyName][item?.key] =
            correctDefaultValue(cvAppName, item?.key, cvAppList, cvappid);
            setIsClick(false);
            setIsTampered(true);
      } else {
        // NPDIOTS-9546: no "" fallback — fields with no schema default (null/undefined)
        // are omitted from the seeded payload instead of being sent as an invalid "".
        const resolved = resolveDefaultFieldValue(scemaTemplateData);
        if (resolved.hasValue) {
          payload["config"][parameterKeyName][item?.key] = resolved.value;
        }
      }
    });
    const existingParams = advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS] || {};
    if (Object.keys(existingParams).length === 0) {
      setAdvancedData({ ...payload });
    } else {
      // Edit flow: advancedData can already carry a saved deploy-parameters blob
      // that only contains keys from ANOTHER form section (e.g. standard-form
      // fields), so it's non-empty but still missing this section's keys. Backfill
      // only what's missing so saved/edited values are never clobbered.
      const merged = mergeMissingDefaults(existingParams, payload.config[parameterKeyName]);
      if (merged) {
        setAdvancedData({
          config: {
            [ADVANCED_CONFIG.DEPLOY_PARAMETERS]: merged,
          },
        });
      }
      // Edit flow: Populate Redux state from advancedData for enrolled appearance
      const enrolledParams = advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.enrolled_appearance_parameters;
      if (window.REACT_APP_ENROLLED_APPEARANCE === "true" && enrolledParams && typeof enrolledParams === 'object') {
        // Dispatch appearances to Redux
        if (Array.isArray(enrolledParams?.appearance) && enrolledParams?.appearance?.length > 0) {
          const appearancesForRedux = enrolledParams?.appearance
            .filter(app => app && app.appearance_id && app.appearance_name)
            .map(app => ({
              value: app.appearance_id,
              name: app.appearance_name
            }));
          if (appearancesForRedux?.length > 0) {
            dispatch(setAdvancedConfigAppearances(appearancesForRedux));
          }
        }
        
        // Dispatch thresholds to Redux
        if (enrolledParams.appearance_capture_threshold !== undefined || 
            enrolledParams.appearance_matching_threshold !== undefined) {
          const thresholds = {
            appearance_capture_threshold: enrolledParams.appearance_capture_threshold,
            appearance_matching_threshold: enrolledParams.appearance_matching_threshold
          };
          dispatch(setAdvancedConfigThresholds(thresholds));
        }
        
        // Dispatch radio selection to Redux
        if (enrolledParams.in_appearance !== undefined) {
          const radioValue = enrolledParams.in_appearance ? "option2" : "option1";
          dispatch(setAdvancedConfigRadio(radioValue));
        }
      }
    }
    // AdvancedConfiguration/MainSection stays mounted for the Polygon page's whole
    // lifetime (the modal only toggles visibility, it doesn't unmount). Re-sync the
    // local schemaData snapshot whenever the data prop changes so switching ROI/app
    // without a remount doesn't render against a stale schema.
    setSchemaData({ ...data });
  }, [data, advancedData, dispatch, cvAppList]);
  return (
    <MainSectionWrapper>
      {action === 'update' && wasEADeployed && (
        <div className="main-section-mb-16">
          <NotificationContainer
            title={EDIT_FLOW_NOTIFICATIONS.COLOR_DETECTION_DISABLED.TITLE}
            subtitle={EDIT_FLOW_NOTIFICATIONS.COLOR_DETECTION_DISABLED.SUBTITLE}
          />
        </div>
      )}
      {schemaData?.scemaResourceData
        ?.filter((d) => {
          const tmpl = schemaData?.scemaTemplateData[d?.key];
          return tmpl && tmpl.display !== false;
        })
        ?.map((item) => {
          const schemaTemplateObject = schemaData?.scemaTemplateData[item?.key];
          return (
            <React.Fragment key={item?.key}>
              {(item?.elementType === "search" ||
                item?.elementType === "text") && (
                <TextInputField
                  style={{
                    maxWidth: "content-width",
                    minWidth: "45%",
                    boxSizing: "border-box",
                  }}
                  schemaTemplateObject={schemaTemplateObject}
                  title={schemaTemplateObject?.title || item?.key}
                  keyName={item?.key}
                  setPayload={setAdvancedData}
                  payload={advancedData}
                  parameterKeyName={parameterKeyName}
                  setIsTampered={setIsTampered}
                  setErrorInFormInput={setErrorInFormInput}
                  cvAppName={cvAppName}
                  description={schemaTemplateObject?.description}
                  cvAppList={cvAppList}
                  cvappid={cvappid}
                />
              )}
              {item?.elementType === "select" && (
                <SelectInputField
                  style={{
                    maxWidth: "content-width",
                    minWidth: "45%",
                    boxSizing: "border-box",
                  }}
                  title={
                    schemaTemplateObject?.title
                      ? schemaTemplateObject?.title
                      : item?.key === "severity"
                      ? "Severity"
                      : item?.key
                  }
                  schemaTemplateObject={schemaTemplateObject}
                  keyName={item?.key}
                  setPayload={setAdvancedData}
                  payload={advancedData}
                  parameterKeyName={parameterKeyName}
                  setIsTampered={setIsTampered}
                />
              )}
              {item?.elementType === "checkboxes" &&
                schemaTemplateObject?.type === "array" && (
                  <CheckboxListField
                    className="main-section-full-width"
                    schemaTemplateObject={schemaTemplateObject}
                    title={schemaTemplateObject?.title || item?.key}
                    keyName={item?.key}
                    setPayload={setAdvancedData}
                    payload={advancedData}
                    parameterKeyName={parameterKeyName}
                    setIsTampered={setIsTampered}
                    setErrorInFormInput={setErrorInFormInput}
                    cvAppName={cvAppName}
                    cvAppList={cvAppList}
                    cvappid = {cvappid}
                  />
                )}
              {item?.elementType === "radios" &&
                schemaTemplateObject?.type === "array" && (
                  <CheckboxListField
                    className="main-section-full-width"
                    schemaTemplateObject={schemaTemplateObject}
                    title={schemaTemplateObject?.title || item?.key}
                    keyName={item?.key}
                    setPayload={setAdvancedData}
                    payload={advancedData}
                    parameterKeyName={parameterKeyName}
                    setIsTampered={setIsTampered}
                    setErrorInFormInput={setErrorInFormInput}
                    cvAppName={cvAppName}
                    cvAppList={cvAppList}
                    cvappid={cvappid}
                  />
                )}
              {item?.elementType === "checkboxes" &&
                schemaTemplateObject?.type === "boolean" && (
                  <CheckboxField
                    className="main-section-full-width"
                    schemaTemplateObject={schemaTemplateObject}
                    title={
                      schemaTemplateObject["title"] ||
                      schemaTemplateObject["notitle"] ||
                      item?.key
                    }
                    keyName={item?.key}
                    setPayload={setAdvancedData}
                    payload={advancedData}
                    parameterKeyName={parameterKeyName}
                    additionalInfo={item?.additionalInfo}
                    setIsTampered={setIsTampered}
                  />
                )}

              {item?.elementType === "toggleswitch" &&
                schemaTemplateObject?.type === "boolean" && (
                  <>
                    {/* Show notification if no licenses available for enrolled appearance */}
                    {/* Hide EA toggle entirely when feature flag is off, or account is v1-only */}
                    {item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && !isFeatureAccessible(isEaFlagEnabled, isV1OnlyAccount) ? null
                    : item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && !hasAvailableLicenses ? (
                      <div style={{ marginBottom: "16px" }}>
                        <NotificationContainer 
                          title={LICENSE_MESSAGES.NO_LICENSE_TITLE}
                          subtitle={LICENSE_MESSAGES.NO_LICENSE_SUBTITLE}
                        />
                      </div>
                    ) : /* EA toggle: hide or show notification when color is active */
                    item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE &&
                     (advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.[ADVANCED_CONFIG.ENABLE_APPEARANCE] || hadColorsDeployed) ? (
                      action === 'update' && hadColorsDeployed ? (
                        <div className="main-section-mb-16">
                          <NotificationContainer
                            title={EDIT_FLOW_NOTIFICATIONS.ENROLLED_APPEARANCE_DISABLED.TITLE}
                            subtitle={EDIT_FLOW_NOTIFICATIONS.ENROLLED_APPEARANCE_DISABLED.SUBTITLE}
                          />
                        </div>
                      ) : null
                    ) : /* Color toggle: hide or show notification when EA is active */
                    item?.key === ADVANCED_CONFIG.ENABLE_APPEARANCE &&
                      (advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.[ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE] || wasEADeployed) ? (
                      null
                    ) : (
                      <ToggleField
                        className="main-section-full-width"
                        schemaTemplateObject={schemaTemplateObject}
                        title={
                          schemaTemplateObject["title"] ||
                          schemaTemplateObject["notitle"] ||
                          item?.key
                        }
                        keyName={item?.key}
                        setPayload={setAdvancedData}
                        payload={advancedData}
                        parameterKeyName={parameterKeyName}
                        additionalInfo={item?.additionalInfo}
                        setIsTampered={setIsTampered}
                        setErrorInFormInput={setErrorInFormInput}
                        action={action}
                        schemaData={schemaData}
                        disabled={
                          (action === 'update' && item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && wasEADeployed) ||
                          (item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && adjustedAvailableLicenses === 0)
                        }
                        licenseInfo={
                          item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE && (
                            <div className="license-info-wrapper">
                              <Badge fillColor="black">{adjustedAvailableLicenses}</Badge>
                              {' '}
                              <TextLink
                                surface="lightPrimary"
                                kind="inliine"
                                onClick={handleLicenseNavigation}
                                size="small"
                                role="link"
                                type="inline"
                              >
                                {ADVANCED_CONFIG.LICENSES_AVAILABLE}
                              </TextLink>
                              {' '}
                              <Tooltip
                                title={ADVANCED_CONFIG.LICENSE_TOOLTIP_DESCRIPTION}
                                placement="bottom-end"
                                arrow
                              >
                                <InfoOutlined fontSize="small" />
                              </Tooltip>
                            </div>
                          )
                        }
                      >
                        {/* Render EnhancedToggleField content inside the same box if enable_enrolled_appearance is enabled */}
                        {item?.key === ADVANCED_CONFIG.ENABLE_ENROLLED_APPEARANCE &&
                         isEnrolledAppearanceClicked(advancedData) && (
                          <div className="main-section-pt-16">
                            <EnhancedToggleField
                              className="main-section-full-width"
                              schemaTemplateObject={schemaTemplateObject?.properties?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS] || schemaTemplateObject}
                              title={
                                schemaTemplateObject?.properties?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS]?.title ||
                                "Enrolled Appearance Parameters"
                              }
                              keyName={ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS}
                              setPayload={setAdvancedData}
                              payload={advancedData}
                              parameterKeyName={parameterKeyName}
                              setIsTampered={setIsTampered}
                              setErrorInFormInput={setErrorInFormInput}
                              cvappid={cvappid}
                              action={action}
                              siteId={siteId}
                            />
                          </div>
                        )}
                      </ToggleField>
                    )}
                  </>
                )}

              {item?.elementType === "radios" &&
                schemaTemplateObject?.type === "boolean" && (
                  <RadioField
                    className="main-section-full-width"
                    schemaData={schemaData}
                    schemaTemplateObject={schemaTemplateObject}
                    title={
                      schemaTemplateObject["title"] ||
                      schemaTemplateObject["notitle"] ||
                      item?.key
                    }
                    keyName={item?.key}
                    setPayload={setAdvancedData}
                    payload={advancedData}
                    parameterKeyName={parameterKeyName}
                    additionalInfo={item?.additionalInfo}
                    setSchemaData={setSchemaData}
                    setIsTampered={setIsTampered}
                  />
                )}

              {item?.elementType === "color" &&
                schemaTemplateObject?.type === "array" &&
                isEnableColorClicked(advancedData) && (
                  <>
                    {/* Show NotificationContainer if enrolled appearance was previously configured in edit flow */}
                    {action === ADVANCED_CONFIG.UPDATE && 
                     advancedData?.config?.[ADVANCED_CONFIG.DEPLOY_PARAMETERS]?.[ADVANCED_CONFIG.ENROLLED_APPEARANCE_PARAMETERS] ? (
                      <div style={{ marginBottom: "16px" }}>
                        <NotificationContainer 
                          title={EDIT_FLOW_NOTIFICATIONS.ENROLLED_APPEARANCE_DISABLED.TITLE}
                          subtitle={EDIT_FLOW_NOTIFICATIONS.ENROLLED_APPEARANCE_DISABLED.SUBTITLE}
                        />
                      </div>
                    ) : (
                      <ColorField
                        colorArray={getValue(schemaTemplateObject, "enum")}
                        payload={advancedData}
                        setPayload={setAdvancedData}
                        keyName={item?.key}
                        parameterKeyName={parameterKeyName}
                        title={
                          getValue(schemaData?.scemaTemplateData, "title") ?? ""
                        }
                        setIsTampered={setIsTampered}
                        cvAppName={cvAppName}
                        setErrorInFormInput={setErrorInFormInput}
                      />
                    )}
                  </>
                )}
              
              {(item?.elementType === "object" ||
                schemaTemplateObject?.type === "object") &&
                item?.elementType !== "enhancedappearance" &&
                <ObjectGroup
                schemaTemplateObject={schemaTemplateObject}
                cvAppName={cvAppName}
                keyName={item?.key}
                parameterKeyName={parameterKeyName}
                payload={advancedData}
                setPayload={setAdvancedData}
                setIsTampered={setIsTampered}
                setErrorInFormInput={setErrorInFormInput}
                cvAppList={cvAppList}
                cvappid = {cvappid}
                />}
            </React.Fragment>
          );
        })}

      {/* Navigation Confirmation Modal */}
      {showNavigationModal && (
        <Modal
          opened={showNavigationModal}
          disableOutsideClick={true}
          surface="light"
          fullScreenDialog={false}
          disableAnimation={false}
          hideCloseButton={false}
          closeButton={
            <div
              className="license-details-modal-close-button"
              onClick={() => {
                setShowNavigationModal(false);
              }}
            >
              <Icon name="close" />
            </div>
          }
        >
          <ModalTitle>
            <Title size="large" bold>{ADVANCED_CONFIG.NAVIGATION_MODAL_TITLE}</Title>
          </ModalTitle>
          <ModalBody>
            <Body size="large">
              {ADVANCED_CONFIG.NAVIGATION_MODAL_MESSAGE}
            </Body>
          </ModalBody>
          <br />
          <br />
          <ButtonGroup
            childWidth={"100%"}
            viewport={"desktop"}
            rowQuantity={{ desktop: 2 }}
            data={[
              {
                children: ADVANCED_CONFIG.NAVIGATION_MODAL_YES,
                size: "medium",
                use: "primary",
                width: "335px",
                onClick: handleConfirmNavigation,
              },
              {
                children: ADVANCED_CONFIG.NAVIGATION_MODAL_NO,
                size: "medium",
                use: "secondary",
                width: "335px",
                onClick: handleCancelNavigation,
              },
            ]}
          />
        </Modal>
      )}
    </MainSectionWrapper>
  );
};
